import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {RuleModel} from '@syncfusion/ej2-querybuilder';
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {EsvgFiles} from "frontier";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {QueryBuilderComponent, QueryBuilderModule, RuleChangeEventArgs} from "@syncfusion/ej2-angular-querybuilder";
import {MatButton} from "@angular/material/button";
import {combineLatest, debounceTime, Observable, of, Subscription, switchMap} from "rxjs";
import {AppService} from "../../../api/generated";
import {getComponent, L10n, loadCldr, setCulture} from '@syncfusion/ej2-base';
import {
  EConditionAttributes,
  ESyncfusionFieldType,
  ETestruleFieldType,
  ITestruleField,
  ITestrulesForm,
  ITestrulesFormValue,
} from './testrules-query-builder-enums-and-interfaces';
import {germanQueryBuilderLocalization} from "./testrules-query-builder-localization-config";
import {
  operatorMapModelToApi
} from "../configuration-overview/verification-rules-config-form/verification-rules-mappings";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DropDownList, DropDownListModule} from '@syncfusion/ej2-angular-dropdowns';
import {catchError} from 'rxjs/operators';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';

declare var require: any;

loadCldr(
  require('cldr-data/supplemental/numberingSystems.json'),
  require('cldr-data/main/de/ca-gregorian.json'),
  require('cldr-data/main/de/numbers.json'),
  require('cldr-data/main/de/timeZoneNames.json'),
  require('cldr-data/supplemental/weekData.json'));

@Component({
  selector: 'app-testrules-query-builder',
  standalone: true,
  templateUrl: './testrules-query-builder.component.html',
  styleUrls: ['./testrules-query-builder.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    QueryBuilderModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatSlideToggle,
    QueryBuilderModule,
    MatButton,
    DropDownListModule,
  ]
})

export class TestrulesQueryBuilderComponent implements OnInit, OnDestroy {

  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;

  fb: FormBuilder = inject(FormBuilder);
  form: FormGroup<ITestrulesForm>;
  query: ModelSignal<RuleModel> = model(null);
  protected subs: Subscription = new Subscription();

  queryBuilderRef: Signal<QueryBuilderComponent> = viewChild<QueryBuilderComponent>(QueryBuilderComponent);
  queryBuilderRef$: Observable<QueryBuilderComponent> = toObservable(this.queryBuilderRef);

  private _snackBar: MatSnackBar = inject(MatSnackBar);
  private _appService: AppService = inject(AppService);

  formValue: ModelSignal<ITestrulesFormValue> = model(null);
  repository: InputSignal<ITestruleField[]> = input(null);
  modifiedRepository: Signal<ITestruleField[]> = computed((): ITestruleField[] => {
    const repository: ITestruleField[] = this.repository();
    return repository.map((col: ITestruleField): ITestruleField => {
      return {
        ...col,
        columns: col.columns.map((innerCol: ITestruleField): ITestruleField => {
          if (innerCol.values && innerCol.type != ESyncfusionFieldType.boolean) {
            return {
              ...innerCol,
              template: {
                create: (): HTMLInputElement => {
                  let elem: HTMLInputElement = document.createElement('input');
                  elem.setAttribute('type', 'text');
                  return elem;
                },
                destroy: (args: { elementId: string }): void => {
                  let dropdown: DropDownList =
                    (getComponent(document.getElementById(args.elementId), 'dropdownlist') as DropDownList);
                  dropdown.destroy();
                },
                write: (args: { elements: Element, values: string[] | string, operator: string }): void => {
                  let ds: string[] = innerCol.values as string[];
                  let dropDownObj: DropDownList = new DropDownList({
                    dataSource: ds,
                    value: args.values as string,
                    placeholder: 'Wert wählen...',
                    change: (e: any): void => {
                      this.queryBuilderRef().notifyChange(e.itemData.value, e.element);
                    }
                  });
                  dropDownObj.appendTo('#' + args.elements.id);
                },
              },
            };
          }
          return innerCol;
        }),
      };
    });

  });
  dropdownValuesMap: Map<string, string[] | number[] | boolean[]> = new Map<string, string[] | number[] | boolean[]>;

  fieldTypeMap: Signal<Map<string, ETestruleFieldType>> = computed((): Map<string, ETestruleFieldType> => {
    const map = new Map<string, ETestruleFieldType>();
    this.modifiedRepository().forEach((item: ITestruleField): void => {
      item.columns.forEach((column: ITestruleField): void => {
        map.set(column.field, column.apiType);
        if (column.values) {
          this.dropdownValuesMap.set(column.field, column.values);
        }
      });
    });
    return map;
  })

  private queryChanged$: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();

  @Output() destroy: EventEmitter<null> = new EventEmitter();
  @Output() duplicate: EventEmitter<null> = new EventEmitter();

  @Output() formValueChanged: EventEmitter<{
    value: string | boolean,
    attribute: EConditionAttributes,
    formValue: ITestrulesFormValue,
  }>
    = new EventEmitter<{ value: string | boolean, attribute: EConditionAttributes, formValue: ITestrulesFormValue }>();
  separator: string = '.';

  constructor() {
    setCulture('de');

    L10n.load(germanQueryBuilderLocalization);

    combineLatest([
      this.queryBuilderRef$,
      this.queryChanged$,
    ]).pipe(
      takeUntilDestroyed(),
      debounceTime(1000),
      switchMap(([queryBuilderRef]): Observable<any> => {
        if (!queryBuilderRef) {
          return of(null);
        }

        if (this.isReadOnly) {
          this._snackBar.open(
            'Sie haben versucht, eine Regel eines Standardprofils zu ändern. Das ist nicht möglich. Bitte duplizieren ' +
            'Sie das Profil und bearbeiten Sie die Kopie, um diese Regel abzuändern.',
            'ok',
            {duration: 5000, verticalPosition: 'top'},
          );
          return of(null);
        }

        const rules: RuleModel = queryBuilderRef.getRules();
        const isValid: boolean = rules.rules.reduce((acc: boolean, current: RuleModel): boolean => {
          return  acc && !!current.field && !!current.label && !!current.type;
        }, true);

        if (!isValid) {
          return of(null);
        }

        const mappedQuery: RuleModel = this.mapOperatorsAndTypes(rules);
        console.log("API-Call wird ausgeführt mit:", mappedQuery);
        return this._appService.conditionTableSetRules({
          condition: this.formValue().controlObj,
          rulechanges: {
            ...mappedQuery,
            guid: this.formValue().ruleGuid,
            parent: this.formValue().parent,
            condition: mappedQuery.condition || "or",
            rules: mappedQuery.rules || [],
          },
        }).pipe(
          catchError((): Observable<null> => {
            this._snackBar.open(
              'Fehler beim Speichern der Regel. Bitte versuchen Sie es erneut.',
              'ok',
              {duration: 5000, verticalPosition: 'top'},
            );
            return of(null);
          }),
        );
      }),
    ).subscribe();
  }

  ngOnInit(): void {
    this.form = this.fb.group<ITestrulesForm>({
      description: this.fb.control(this.formValue().description),
      active: this.fb.control(this.formValue().active),
    });

    this.subs.add(
      this.form.valueChanges.subscribe((value: Partial<ITestrulesFormValue>): void => {
        this.formValue.set({
          ...this.formValue(),
          description: value.description,
          active: value.active,
        });
      })
    );

    if (this.isReadOnly) {
      this.form.controls.active.disable({emitEvent: false});
    } else {
      this.form.controls.active.enable({emitEvent: false});
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get isReadOnly(): boolean {
    return this.formValue()?.controlObj?.signature == null;
  }

  deleteTestrule(): void {
    this.destroy.emit();
  }

  duplicateTestrule(): void {
    this.duplicate.emit();
  }

  changeDescription(): void {
    this.formValueChanged.emit({
      value: this.formValue().description,
      attribute: EConditionAttributes.description,
      formValue: this.formValue(),
    });
  }

  changeActive(): void {
    this.formValueChanged.emit({
      value: this.formValue().active,
      attribute: EConditionAttributes.active,
      formValue: this.formValue(),
    });
  }

  onQueryChanged(event: RuleChangeEventArgs): void {
    console.log("Query changed:", event.rule);
    this.queryChanged$.emit(event.rule);
  }

  mapOperatorsAndTypes(rule: RuleModel): RuleModel {
    const ruleField: string = rule.field;
    const fieldTypeMap: Map<string, ETestruleFieldType> = this.fieldTypeMap();
    return {
      ...rule,
      operator: rule.operator ? operatorMapModelToApi[rule.operator] || rule.operator : rule.operator,
      type: fieldTypeMap.get(ruleField) || undefined,
      rules: rule.rules ? rule.rules.map((r: RuleModel): RuleModel => this.mapOperatorsAndTypes(r)) : rule.rules,
    };
  }
}
